import { makeEntityId } from '@web-platform/shared-universal-types-utils/src/utils';
import { nonNullishOrThrow } from '@web-platform/shared-universal-utils-frontend/src/helpers/assertNonNullish';
import { sonarLocalDateTimeToISOString } from '@web-platform/shared-universal-utils-frontend/src/helpers/grpcLocalDateTimeToISOString';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { mapSonarV3AmenityInfo } from './sonarV3AmenityInfoMapper';

import type { SkyscannerSonarV3Response } from '@skyscanner-internal/sonar-web-http-client';
import type { SegmentBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/segmentsBookingPanel';

type Leg = SkyscannerSonarV3Response.Leg;

export const mapSonarV3Segments = (legs?: Leg[]): SegmentBookingPanel[] => {
  const segmentIds = new Set();
  const segments: SegmentBookingPanel[] = [];

  try {
    nonNullishOrThrow(legs, 'legs').forEach((leg) => {
      nonNullishOrThrow(leg.segments, 'leg.segments').forEach((segment) => {
        const segmentId = nonNullishOrThrow(segment.id, 'segment.id');
        if (!segmentIds.has(segmentId)) {
          segments.push({
            id: segmentId,
            origin_place_id: nonNullishOrThrow(
              segment.origin?.airport?.altId,
              'segment.origin.airport.altId',
            ),
            origin_entity_id: makeEntityId(
              nonNullishOrThrow(
                segment.origin?.airport?.entityId,
                'segment.origin.airport.entityId',
              ),
            ),
            destination_place_id: nonNullishOrThrow(
              segment.destination?.airport?.altId,
              'segment.destination.airport.altId',
            ),
            destination_entity_id: makeEntityId(
              nonNullishOrThrow(
                segment.destination?.airport?.entityId,
                'segment.destination.airport.entityId',
              ),
            ),
            departure: sonarLocalDateTimeToISOString(
              nonNullishOrThrow(segment.departure, 'segment.departure'),
            ),
            arrival: sonarLocalDateTimeToISOString(
              nonNullishOrThrow(segment.arrival, 'segment.arrival'),
            ),
            duration: nonNullishOrThrow(segment.durationMinutes, 'segment.durationMinutes'),
            marketing_flight_number: nonNullishOrThrow(
              segment.flightNumber,
              'segment.flightNumber',
            ),
            marketing_carrier_id: nonNullishOrThrow(
              segment.marketingCarrier?.id,
              'segment.marketingCarrier.id',
            ),
            operating_carrier_id: nonNullishOrThrow(
              segment.operatingCarrier?.id,
              'segment.operatingCarrier.id',
            ),
            amenity_info: mapSonarV3AmenityInfo(segment.amenityInfo),
          });

          segmentIds.add(segmentId);
        }
      });
    });

    return segments;
  } catch (error) {
    logError({
      component: 'mapSonarV3Segments',
      message: `SONARV3: Error mapping V3 segments`,
      data: JSON.stringify(legs),
      error,
      stack: error?.stack,
    });
  }

  return [];
};
